import { useMemo, useState } from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS } from "~/types/contabilidad";
import type { LibroMayorLine } from "~/types/contabilidad";

interface Balance8ColumnasTableProps {
  lines: LibroMayorLine[];
  isLoading: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function Balance8ColumnasTable({
  lines,
  isLoading,
}: Balance8ColumnasTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const totalPages = Math.ceil(lines.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLines = lines.slice(startIndex, endIndex);

  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        const debitos = Number(line.debitos_periodo);
        const creditos = Number(line.creditos_periodo);
        return {
          saldo_inicial: acc.saldo_inicial + Number(line.saldo_inicial),
          debitos_periodo: acc.debitos_periodo + debitos,
          creditos_periodo: acc.creditos_periodo + creditos,
          movimiento_neto: acc.movimiento_neto + (debitos - creditos),
          saldo_final: acc.saldo_final + Number(line.saldo_final),
        };
      },
      {
        saldo_inicial: 0,
        debitos_periodo: 0,
        creditos_periodo: 0,
        movimiento_neto: 0,
        saldo_final: 0,
      }
    );
  }, [lines]);

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay movimientos para las cuentas en el periodo seleccionado.
        </p>
        <p className="text-sm text-muted-foreground">
          Selecciona una empresa y un periodo diferente o verifica que existan
          comprobantes publicados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Codigo</TableHead>
              <TableHead>Cuenta</TableHead>
              <TableHead className="w-[100px]">Tipo</TableHead>
              <TableHead className="w-[120px] text-right">
                Saldo Inicial
              </TableHead>
              <TableHead className="w-[120px] text-right">Debitos</TableHead>
              <TableHead className="w-[120px] text-right">Creditos</TableHead>
              <TableHead className="w-[130px] text-right">
                Movimiento Neto
              </TableHead>
              <TableHead className="w-[120px] text-right">
                Saldo Final
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedLines.map((line, index) => {
              const movimientoNeto =
                Number(line.debitos_periodo) - Number(line.creditos_periodo);
              return (
                <TableRow
                  key={line.account_id}
                  className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  style={{
                    animationDelay: `${Math.min(index * 40, 400)}ms`,
                  }}
                >
                  <TableCell className="font-mono text-sm font-medium">
                    {line.account_code}
                  </TableCell>
                  <TableCell>{line.account_name}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        ACCOUNT_TYPE_COLORS[line.account_type] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {ACCOUNT_TYPE_LABELS[line.account_type] ||
                        line.account_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCurrency(Number(line.saldo_inicial))}
                  </TableCell>
                  <TableCell className="text-right font-mono text-green-600">
                    {formatCurrency(Number(line.debitos_periodo))}
                  </TableCell>
                  <TableCell className="text-right font-mono text-blue-600">
                    {formatCurrency(Number(line.creditos_periodo))}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono font-medium ${
                      movimientoNeto >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {formatCurrency(movimientoNeto)}
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    {formatCurrency(Number(line.saldo_final))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-muted/50 font-semibold">
              <TableCell colSpan={3}>TOTALES</TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totals.saldo_inicial)}
              </TableCell>
              <TableCell className="text-right font-mono text-green-600">
                {formatCurrency(totals.debitos_periodo)}
              </TableCell>
              <TableCell className="text-right font-mono text-blue-600">
                {formatCurrency(totals.creditos_periodo)}
              </TableCell>
              <TableCell
                className={`text-right font-mono font-medium ${
                  totals.movimiento_neto >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {formatCurrency(totals.movimiento_neto)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totals.saldo_final)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Mostrar</span>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>por pagina</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {startIndex + 1}-{Math.min(endIndex, lines.length)} de{" "}
            {lines.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
